import { get, head, last } from 'lodash'
import { resolveInitialLocale, setLanguage } from '@/shared/helpers/language'

import { isBranded } from '@/helpers/isBranded'
import { mapVideo, mapAiVideos } from '@/store/modules/tour/mappers'

const state = {
  title: '',
  ready: false,
  success: false,
  error: false,
  id: undefined,
  settings: {},
  photos: [],
  tour360: {
    matterport: false,
    iguide: false,
    cloudPano: false,
    matterportUrl: '',
    iguideUrl: '',
    cloudPanoUrl: ''
  },
  video: {
    isYoutube: false,
    isVimeo: false,
    isIframe: false,
    youtubeUrlBranded: '',
    youtubeUrlUnbranded: '',
    vimeoUrlBranded: '',
    vimeoUrlUnbranded: '',
    iframeUrlBranded: '',
    iframeUrlUnbranded: ''
  },
  address: {},
  fullAddress: undefined,
  walkScore: {},
  map: {},
  agent: {},
  multiAgents: [],
  details: {},
  floorplan: {},
  documents: [],
  footer: {},
  payments: {
    enabled: false,
    paid: true,
    paymentLink: ''
  },
  branded: false
}

const getters = {
  getReady (state) {
    return state.ready
  },
  getPhotos() {
    return state.photos.map(item => ({
      hidden: !!item.hidden,
      original: get(item.urls.find(el => el.size === 'orig'), 'url'),
      xs: get(item.urls.find(el => el.size === '500x380'), 'url'),
      md: get(item.urls.find(el => el.size === '1024x1024'), 'url'),
      xl: get(item.urls.find(el => el.size === '2560x1440'), 'url')
        || get(item.urls.find(el => el.size === '1920x1080'), 'url')
        || get(item.urls.find(el => el.size === '1600x900'), 'url')
    })).filter(item => !item.hidden);
  },
  getPhotoByIndex (state) {
    return index => {
      return state.photos[index]
    }
  },
  getPhotosByLimit (state) {
    return (limit) => {
      return state.photos.slice(0, limit)
    }
  },
  getLayoutType (state) {
    const name = head(state.settings.layout.split('-'))

    if (name === 'scissors') {
      return 'multi'
    }

    return 'one'
  },
  getLayoutName (state) {
    const {layout} = state.settings

    if (layout) {
      return head(layout.split('-'))
    }

    return undefined
  },
  getThemeName (state) {
    const {layout} = state.settings

    if (layout) {
      return last(layout.split('-'))
    }

    return 'light'
  },
  getBranded (state) {
    return state.branded
  },
  getUnpaid (state) {
    return state.payments.enabled && !state.payments.paid
  }
}

const mutations = {
  setTitle (state, title) {
    state.title = title
  },
  setReady (state) {
    state.ready = true
  },
  setSuccess (state) {
    state.success = true
  },
  setError (state) {
    state.error = true
  },
  setId (state, id) {
    state.id = id
  },
  setSettings (state, settings) {
    state.settings = settings
  },
  setPhotos (state, photos) {
    state.photos = photos
  },
  setTour360 (state, tour360) {
    state.tour360 = tour360
  },
  setVideo (state, video) {
    state.video = video
  },
  setAddress (state, address) {
    state.address = address
  },
  setFullAddress (state, fullAddress) {
    state.fullAddress = fullAddress
  },
  setWalkScore (state, walkScore) {
    state.walkScore = walkScore
  },
  setMap (state, map) {
    state.map = map
  },
  setAgent (state, agent) {
    state.agent = agent
  },
  setMultiAgents (state, multiAgents) {
    state.multiAgents = multiAgents
  },
  setDetails (state, details) {
    state.details = details
  },
  setFloorplan (state, floorplan) {
    state.floorplan = floorplan
  },
  setDocuments (state, documents) {
    state.documents = documents
  },
  setFooter (state, footer) {
    state.footer = footer
  },
  setBranded (state, branded) {
    state.branded = branded
  },
  setPayments (state, payments) {
    state.payments = Object.assign({}, state.payments, payments)
  }
}

const actions = {
  getTourSuccess ({commit, dispatch}, data) {
    const {
      id,
      settings,
      branding,
      photos,
      tour360,
      video,
      address,
      fullAddress,
      walkScore,
      map,
      agent,
      multiAgents,
      details,
      floorplan,
      documents,
      footer,
      payments
    } = data
    const aiVideos = mapAiVideos(data.aiVideos, settings)

    commit('setSuccess')
    commit('setId', id)
    commit('setSettings', settings)
    photos && commit('setPhotos', photos)
    tour360 && commit('setTour360', tour360)
    video && commit('setVideo', video)
    aiVideos.length && commit('setVideo', { ...video, videos: [...((video && mapVideo(video)) || []), ...aiVideos] })
    commit('setAddress', address)
    commit('setFullAddress', fullAddress)
    walkScore && commit('setWalkScore', walkScore)
    map && commit('setMap', map)
    agent && commit('setAgent', agent)
    multiAgents && commit('setMultiAgents', multiAgents)
    details && commit('setDetails', details)
    floorplan && commit('setFloorplan', floorplan)
    documents && commit('setDocuments', documents)
    footer && commit('setFooter', footer)
    payments && commit('setPayments', payments)

    setLanguage(resolveInitialLocale(branding.lang))
    dispatch('setReady')
  },
  getTourFailure ({commit, dispatch}) {
    commit('setError')
    dispatch('setReady')
  },
  setBranded ({commit}, branded) {
    commit('setBranded', branded)
  },
  setReady ({commit}) {
    setTimeout(() => {
      commit('setReady')

      const { fullAddress } = state

      if (!isBranded() && fullAddress) {
        commit('setTitle', fullAddress)
      }
    }, 2000)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
